import { useMemo } from 'react';
import { create } from 'zustand';
import { applyWrite } from '@/shared/lib/store-write';
import { storeNotice } from '@/shared/lib/store-notice';
import { describeBootstrapFailure } from '@/modules/workspaces/personal-bootstrap';
import { coreApi } from '@/integrations/api/active';
import { said } from '@/integrations/api/client';
import { supabase } from '@/integrations/supabase/client';
import { workspaceHasFeature, type SubscriptionTier } from '@/modules/workspaces/workspace-features';
import {
  agendaPresetFor, isBuiltInPreset, planPreset, presetWrite, terminologyPresetFor, visionTemplateFor,
  type PresetCategory, type PresetContext, type PresetKey,
} from '@/modules/presets/presets';
import { isPersonalAnswers, isPracticeAnswersDraft, type OnboardingDraft } from '@/modules/onboarding/onboarding-paths';
import { countBillableSeats } from '@/modules/billing/seat-count';
import {
  DEFAULT_WORKSPACE_MEETING_LENGTH,
  DEFAULT_WORKSPACE_SCORECARD_SETTINGS,
  DEFAULT_WORKSPACE_SECTION_LENGTHS,
} from '@/modules/workspaces/workspace-settings';
import type { WorkspaceRole, WorkspaceSettings } from '@/modules/workspaces/types';
import type { Cadence } from '@/modules/scorecards/types';
import {
  ANNUAL_MEETING_SECTIONS,
  BUILTIN_MEETING_TYPES,
  BUILTIN_SECTION_KEYS,
  QUARTERLY_MEETING_SECTIONS,
  WEEKLY_MEETING_SECTIONS,
  type MeetingSectionDef,
  type MeetingType,
} from '@/modules/meetings/types';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | null;

// Attempted once per identity, not once per page: sessions can be switched
// without a reload, and the identity switched *to* needs its own workspace as
// much as the one switched from. Held per user id so one failure does not stop
// the next identity trying, and so a repeated failure is not retried on every
// fetch. A failure is therefore final for the page: the reload that clears this
// set is the retry, which is what the notice tells the user to do (#338).
const _personalBootstrapAttempted = new Set<string>();
let _workspaceDataRequestGeneration = 0;
let _workspaceCatalogRequestGeneration = 0;
let _priceCohortRequestGeneration = 0;

const EMPTY_ONBOARDING: OnboardingDraft = {
  answers: null, recommendedPath: null, chosenPath: null, cycleIntent: null, recordedAt: null,
};

function onboardingFromRow(row: any): OnboardingDraft {
  return {
    answers: isPracticeAnswersDraft(row?.onboarding_answers) || isPersonalAnswers(row?.onboarding_answers) ? row.onboarding_answers : null,
    recommendedPath: row?.onboarding_recommended_path ?? null,
    chosenPath: row?.onboarding_chosen_path ?? null,
    cycleIntent: row?.onboarding_cycle_intent ?? null,
    recordedAt: row?.onboarding_recorded_at ?? null,
  };
}

const ONBOARDING_COLUMNS: Record<keyof OnboardingDraft, string> = {
  answers: 'onboarding_answers',
  recommendedPath: 'onboarding_recommended_path',
  chosenPath: 'onboarding_chosen_path',
  cycleIntent: 'onboarding_cycle_intent',
  recordedAt: 'onboarding_recorded_at',
};

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  ownerUserId: string;
  /** Active cycle: which year (and optional quarter) the workspace currently treats as "current". */
  activeYear?: number | null;
  activeQuarter?: number | null;
  status?: 'active' | 'pending_approval' | 'rejected';
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  priceCohort: string | null;
  /** Cached seat quantity from Stripe, synced by stripe-webhook. Null = no subscription (free tier). */
  purchasedSeats: number | null;
  /** Cached renewal date and current-period total from Stripe, synced by stripe-webhook. */
  renewalDate: string | null;
  currentPeriodTotal: number | null;
  /** True when the subscription is set to cancel at period end (e.g. cancelled during trial). */
  cancelAtPeriodEnd: boolean;
  /** Set when billable seat count exceeds purchased_seats; null when compliant. */
  seatOverageSince: string | null;
  /** A workspace of one, bootstrapped per identity (#296). Never deletable, never invitable. */
  isPersonal: boolean;
}

/** One pricing element of a cohort — flat/per_seat/metered/quota. See price_cohort_components table. */
export interface PriceCohortComponent {
  id: string;
  cohortSlug: string;
  pricingModel: 'flat' | 'per_seat' | 'metered' | 'quota';
  cadence: 'monthly' | 'annual';
  stripePriceId: string | null;
  includedQuantity: number | null;
  overageStartsAt: number | null;
  cap: number | null;
  quotaResetInterval: string | null;
  meteredUnitKey: string | null;
  backfilledAt: string | null;
}

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  personId: string | null;
  role: WorkspaceRole;
  email: string | null;
}

export interface WorkspaceInvitation {
  id: string;
  workspaceId: string;
  email: string;
  role: WorkspaceRole;
  invitedBy: string;
  status: string; // 'pending' | 'accepted' | 'declined'
  personId?: string | null;
  createdAt: string;
}

function deserializeMeetingType(r: any): MeetingType {
  return {
    id: r.id,
    teamId: String(r.team_id ?? r.teamId ?? ''),
    key: String(r.key),
    // null must survive: like sections, it is the inherit state. Coercing it to
    // the key here made every inheriting row claim a local label equal to its
    // own key, which is how lowercase slugs reached the UI.
    label: r.label == null ? null : String(r.label),
    builtIn: !!(r.built_in ?? r.builtIn),
    sortOrder: Number(r.sort_order ?? r.sortOrder ?? 0),
    // null must survive: it is the inherit state, not a missing value.
    sections: Array.isArray(r.sections)
      ? r.sections.map((s: any) => ({
          key: String(s.key),
          label: String(s.label ?? s.key),
          length: Number(s.length ?? 5),
          custom: !!s.custom,
        }))
      : null,
    fathomMeetingTitle: r.fathom_meeting_title != null
      ? String(r.fathom_meeting_title)
      : r.fathomMeetingTitle != null ? String(r.fathomMeetingTitle) : undefined,
    recurrence: r.recurrence ?? undefined,
    notificationTimingHours: Number(r.notification_timing_hours ?? r.notificationTimingHours ?? 24),
    prepEmailEnabled: !!(r.prep_email_enabled ?? r.prepEmailEnabled ?? false),
    defaultInvitees: r.default_invitees == null ? null
      : Array.isArray(r.default_invitees) ? r.default_invitees : [],
    defaultHostId: r.default_host_id ?? r.defaultHostId ?? undefined,
  };
}

export interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspaceId: string | null;
  currentWorkspace: Workspace | null;
  members: WorkspaceMember[];
  invitations: WorkspaceInvitation[];
  settings: WorkspaceSettings | null;
  /** Meeting types loaded from the meeting_types table. One row per type per workspace. */
  meetingTypes: MeetingType[];
  /** All price_cohort_components rows for the current workspace's price_cohort. */
  priceCohortComponents: PriceCohortComponent[];
  myRole: WorkspaceRole | null;
  /** The person id of the current logged-in user in this workspace, or null if unlinked. */
  myPersonId: string | null;
  /** False while fetchWorkspaceData is in flight for the current workspace — myRole/members/settings aren't final yet. */
  workspaceDataReady: boolean;
  /**
   * Whose data this store currently holds. The store is a module singleton and
   * survives sign-out, so without this a second user landing on the same slug
   * inherits the first user's `myRole` until their own fetch resolves — long
   * enough to flash admin-only UI at a member.
   */
  dataUserId: string | null;
  loading: boolean;

  /**
   * Drop everything resolved for a previous user. Called when the authenticated
   * user changes — NOT on a token refresh, which keeps the same id.
   */
  resetForUserChange: () => void;
  fetchWorkspaces: () => Promise<void>;
  setCurrentWorkspace: (slug: string) => Promise<void>;
  fetchWorkspaceData: (workspaceId: string) => Promise<void>;
  fetchPriceCohortComponents: (cohortSlug: string) => Promise<void>;
  createWorkspace: (name: string, slug: string, userId: string, ownerDisplayName?: string | null, ownerTimezone?: string) => Promise<Workspace>;
  acceptInvitation: (invitationId: string, userId: string) => Promise<{ success: boolean; error?: string; workspaceSlug?: string }>;
  declineInvitation: (invitationId: string) => Promise<{ success: boolean; error?: string }>;
  inviteMember: (email: string, role: WorkspaceRole, personId?: string | null) => Promise<{ id: string | null; suppressed?: 'already_member' | 'already_invited'; emailFailed?: boolean; error?: string }>;
  updateInvitationRole: (invitationId: string, role: WorkspaceRole) => Promise<void>;
  deleteInvitation: (invitationId: string) => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<{ emailFailed?: boolean; error?: string }>;
  reInvite: (invitationId: string) => Promise<{ emailFailed?: boolean; error?: string }>;
  updateMemberRole: (memberId: string, role: WorkspaceRole) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  linkMemberToPerson: (memberId: string, personId: string | null) => Promise<void>;
  updateSettings: (updates: Partial<WorkspaceSettings>) => Promise<void>;
  applyPreset: (
    presetKey: PresetKey | null,
    replace?: Partial<Record<PresetCategory, boolean>>,
  ) => Promise<{ visionApplied: boolean }>;
  saveOnboarding: (patch: Partial<OnboardingDraft>, workspaceId?: string) => Promise<void>;
  addMeetingType: (type: Omit<MeetingType, 'id'>) => Promise<void>;
  updateMeetingType: (id: string, updates: Partial<Omit<MeetingType, 'id' | 'key' | 'builtIn'>>) => Promise<void>;
  deleteMeetingType: (id: string) => Promise<void>;
  transferOwnership: (newOwnerMemberId: string) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
}

export type WorkspaceCatalogContract = Pick<WorkspaceState,
  | 'workspaces' | 'currentWorkspaceId' | 'currentWorkspace' | 'loading'
  | 'fetchWorkspaces' | 'setCurrentWorkspace' | 'createWorkspace' | 'deleteWorkspace'
>;

export type WorkspaceMembershipContract = Pick<WorkspaceState,
  | 'members' | 'invitations' | 'myRole' | 'myPersonId' | 'workspaceDataReady' | 'dataUserId'
  | 'resetForUserChange' | 'fetchWorkspaceData' | 'acceptInvitation' | 'declineInvitation'
  | 'inviteMember' | 'updateInvitationRole' | 'deleteInvitation' | 'resendInvitation' | 'reInvite'
  | 'updateMemberRole' | 'removeMember' | 'linkMemberToPerson' | 'transferOwnership'
>;

export type WorkspaceSettingsContract = Pick<WorkspaceState, 'settings' | 'updateSettings'>;

export type WorkspaceMeetingTypesContract = Pick<WorkspaceState,
  'meetingTypes' | 'addMeetingType' | 'updateMeetingType' | 'deleteMeetingType'
>;

export type WorkspaceBillingContract = Pick<WorkspaceState,
  'priceCohortComponents' | 'fetchPriceCohortComponents'
>;

/**
 * Workspace-owned server cache for catalog/selection, membership/invitations,
 * settings/meeting types, and billing cohort data. `dataUserId` guards account
 * ownership; `currentWorkspaceId` guards workspace-scoped publication.
 */
export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  currentWorkspaceId: null,
  currentWorkspace: null,
  members: [],
  invitations: [],
  settings: null,
  meetingTypes: [],
  priceCohortComponents: [],
  myRole: null,
  myPersonId: null,
  workspaceDataReady: false,
  dataUserId: null,
  loading: false,

  resetForUserChange: () => {
    _workspaceDataRequestGeneration += 1;
    _workspaceCatalogRequestGeneration += 1;
    _priceCohortRequestGeneration += 1;
    set({
      workspaces: [], currentWorkspace: null, currentWorkspaceId: null,
      members: [], invitations: [], settings: null, meetingTypes: [],
      myRole: null, myPersonId: null, workspaceDataReady: false, dataUserId: null,
    });
  },

  fetchWorkspaces: async () => {
    const requestGeneration = ++_workspaceCatalogRequestGeneration;
    set({ loading: true });
    const { data } = await supabase.from('workspaces').select('*');
    if (requestGeneration !== _workspaceCatalogRequestGeneration) return;
    const workspaces: Workspace[] = (data ?? []).map((w: any) => ({
      id: w.id, name: w.name, slug: w.slug, ownerUserId: w.owner_user_id,
      activeYear: (w as any).active_year ?? null,
      activeQuarter: (w as any).active_quarter ?? null,
      status: (w as any).status ?? 'active',
      subscriptionTier: ((w as any).subscription_tier ?? 'free') as SubscriptionTier,
      subscriptionStatus: ((w as any).subscription_status ?? null) as SubscriptionStatus,
      stripeCustomerId: (w as any).stripe_customer_id ?? null,
      stripeSubscriptionId: (w as any).stripe_subscription_id ?? null,
      priceCohort: (w as any).price_cohort ?? null,
      purchasedSeats: (w as any).purchased_seats ?? null,
      renewalDate: (w as any).renewal_date ?? null,
      cancelAtPeriodEnd: (w as any).cancel_at_period_end ?? false,
      currentPeriodTotal: (w as any).current_period_total ?? null,
      seatOverageSince: (w as any).seat_overage_since ?? null,
      isPersonal: (w as any).is_personal ?? false,
    }));
    set({ workspaces, loading: false });

    // Where a personal workspace comes from. Not a trigger on `auth.users`:
    // that row exists as soon as a magic link is requested, so every mistyped
    // address would get a workspace, and GoTrue's own transaction cannot commit
    // one anyway (see the migration). This is the first authenticated load —
    // the point where somebody has actually arrived — and it covers every route
    // in: a free or paid signup, a login with no workspace, an abandoned
    // workspace creation, and an invitation accepted or still pending.
    //
    // The unique index makes a redundant call a no-op.
    if (!workspaces.some((w) => w.isPersonal)) {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      // Marked only once there is an identity to mark. Marking before the
      // session resolved spent the attempt on a fetch that could never have
      // made the call, and nothing tried again until a full reload.
      if (userId && !_personalBootstrapAttempted.has(userId)) {
        _personalBootstrapAttempted.add(userId);
        const { error } = await coreApi('/personal-workspace/ensure');
        if (error) {
          console.error('[fetchWorkspaces] personal workspace bootstrap failed', error);
          // Whether somebody has a workspace at all is not something to fail
          // quietly about (#338). The attempt is not retried automatically —
          // the set above holds for the session — so the notice says what
          // does retry it.
          const failure = describeBootstrapFailure(error);
          storeNotice.error(failure.message, { description: failure.description });
        } else {
          await get().fetchWorkspaces();
        }
      }
    }
  },

  setCurrentWorkspace: async (slug) => {
    const { workspaces } = get();
    let ws = workspaces.find((w) => w.slug === slug);
    if (!ws) {
      await get().fetchWorkspaces();
      ws = get().workspaces.find((w) => w.slug === slug);
    }
    if (ws) {
      _workspaceDataRequestGeneration += 1;
      set({ currentWorkspaceId: ws.id, currentWorkspace: ws, myRole: null, workspaceDataReady: false, settings: null });
      // Don't load workspace data for non-active workspaces
      if (!ws.status || ws.status === 'active') {
        await get().fetchWorkspaceData(ws.id);
      } else {
        set({ workspaceDataReady: true });
      }
    }
  },

  fetchWorkspaceData: async (workspaceId) => {
    const requestGeneration = ++_workspaceDataRequestGeneration;
    const [{ data: membersData }, { data: invData }, { data: settingsData }, { data: meetingTypesData }] = await Promise.all([
      supabase.from('workspace_members').select('*').eq('workspace_id', workspaceId),
      supabase.from('workspace_invitations').select('*').eq('workspace_id', workspaceId),
      supabase.from('workspace_settings').select('*').eq('workspace_id', workspaceId).maybeSingle(),
      supabase.from('meeting_types').select('*').eq('workspace_id', workspaceId).order('sort_order'),
    ]);

    const memberUserIds = (membersData ?? []).map((m: any) => m.user_id).filter(Boolean);
    const { data: profilesData } = memberUserIds.length > 0
      ? await supabase.from('profiles').select('id, email').in('id', memberUserIds)
      : { data: [] as any[] };
    const profileEmailById: Record<string, string> = {};
    for (const p of (profilesData ?? [])) profileEmailById[p.id] = p.email;

    const members: WorkspaceMember[] = (membersData ?? []).map((m: any) => ({
      id: m.id, workspaceId: m.workspace_id, userId: m.user_id,
      personId: m.person_id, role: m.role as WorkspaceRole,
      email: profileEmailById[m.user_id] ?? null,
    }));

    const invitations: WorkspaceInvitation[] = (invData ?? []).map((i: any) => ({
      id: i.id, workspaceId: i.workspace_id, email: i.email,
      role: i.role as WorkspaceRole, invitedBy: i.invited_by, status: i.status ?? 'pending',
      personId: i.person_id ?? null, createdAt: i.created_at,
    }));

    const { data: { session } } = await supabase.auth.getSession();
    const myMembership = members.find((m) => m.userId === session?.user?.id);

    const meetingTypes: MeetingType[] = meetingTypesData && meetingTypesData.length > 0
      ? meetingTypesData.map(deserializeMeetingType)
      : BUILTIN_MEETING_TYPES;
    const weeklyDP = ((settingsData as any)?.meeting_data_period_weekly ?? (settingsData as any)?.meeting_data_period ?? 'previous') as 'previous'|'current';
    const monthlyDP = ((settingsData as any)?.meeting_data_period_monthly ?? 'previous') as 'previous'|'current';
    const quarterlyDP = ((settingsData as any)?.meeting_data_period_quarterly ?? 'previous') as 'previous'|'current';
    const yearlyDP = ((settingsData as any)?.meeting_data_period_yearly ?? 'previous') as 'previous'|'current';
    const settings: WorkspaceSettings | null = settingsData ? {
      id: settingsData.id,
      workspaceId: settingsData.workspace_id,
      successThreshold: Number(settingsData.success_threshold),
      dangerZoneWeeks: settingsData.danger_zone_weeks,
      defaultMeetingLength: settingsData.default_meeting_length,
      sectionLengths: settingsData.section_lengths as any,
      startOfWeek: ((settingsData as any).start_of_week ?? 1) as 0|1|2|3|4|5|6,
      firstWeekRule: ((settingsData as any).first_week_rule ?? 'touching') as 'touching'|'full',
      meetingDataPeriod: weeklyDP,
      meetingDataPeriodWeekly: weeklyDP,
      meetingDataPeriodMonthly: monthlyDP,
      meetingDataPeriodQuarterly: quarterlyDP,
      meetingDataPeriodYearly: yearlyDP,
      terminology: (settingsData as any).terminology ?? null,
      terminologyPlurals: (settingsData as any).terminology_plurals ?? null,
      terminologyPreset: (settingsData as any).terminology_preset ?? null,
      agendaPreset: (settingsData as any).agenda_preset ?? null,
      queuedStatesEnabled: Boolean((settingsData as any).queued_states_enabled ?? false),
      cyclicalPlanning: Boolean((settingsData as any).cyclical_planning ?? true),
      cycleDecidedAt: (settingsData as any).cycle_decided_at ?? null,
      onboarding: onboardingFromRow(settingsData),
      loosePermissions: Boolean((settingsData as any).loose_permissions ?? false),
      visionTemplate: (settingsData as any).vision_template ?? null,
      homeVisionFieldId: (settingsData as any).home_vision_field_id ?? null,
      homeValuesFieldId: (settingsData as any).home_values_field_id ?? null,
      showValueDescriptions: (settingsData as any).show_value_descriptions ?? true,
      paceYellowThreshold: Number((settingsData as any).pace_yellow_threshold ?? 10),
      paceRedThreshold: Number((settingsData as any).pace_red_threshold ?? 25),
      timezone: (settingsData as any).timezone ?? 'UTC',
      stakeholderFrameworkId: (settingsData as any).stakeholder_framework_id ?? 'raci',
      stakeholderFrameworks: Array.isArray((settingsData as any).stakeholder_frameworks)
        ? (settingsData as any).stakeholder_frameworks
        : [],
      setupChecklist: (() => {
        const rawChecklist = (settingsData as any).setup_checklist;
        if (!rawChecklist) return null;
        return {
          ...rawChecklist,
          items: (rawChecklist.items ?? []).map((item: any) => ({
            key: item.key,
            completed: item.completed !== undefined
              ? Boolean(item.completed)
              : item.status === 'completed',
          })),
        };
      })(),
      showAvatars: Boolean((settingsData as any).show_avatars ?? false),
      orgChartBoardEnabled: Boolean((settingsData as any).org_chart_board_enabled ?? false),
      metricIndicatorsEnabled: Boolean((settingsData as any).metric_indicators_enabled ?? true),
      aiInsightsEnabled: Boolean((settingsData as any).ai_insights_enabled ?? false),
      aiInsightsVisibility: (settingsData as any).ai_insights_visibility === 'restricted' ? 'restricted' : 'everyone',
      connectedAppsEnabled: Boolean((settingsData as any).connected_apps_enabled ?? false),
      connectedAppsWriteEnabled: Boolean((settingsData as any).connected_apps_write_enabled ?? false),
    } : null;

    if (
      requestGeneration !== _workspaceDataRequestGeneration
      || get().currentWorkspaceId !== workspaceId
    ) return;

    set({ members, invitations, settings, meetingTypes, myRole: myMembership?.role as WorkspaceRole ?? null, myPersonId: myMembership?.personId ?? null, workspaceDataReady: true, dataUserId: session?.user?.id ?? null });

    const cohortSlug = get().workspaces.find((w) => w.id === workspaceId)?.priceCohort;
    if (cohortSlug) get().fetchPriceCohortComponents(cohortSlug);
  },

  fetchPriceCohortComponents: async (cohortSlug) => {
    const requestGeneration = ++_priceCohortRequestGeneration;
    const { data } = await supabase
      .from('price_cohort_components')
      .select('*')
      .eq('cohort_slug', cohortSlug);
    if (
      requestGeneration !== _priceCohortRequestGeneration
      || get().currentWorkspace?.priceCohort !== cohortSlug
    ) return;
    const components: PriceCohortComponent[] = (data ?? []).map((c: any) => ({
      id: c.id,
      cohortSlug: c.cohort_slug,
      pricingModel: c.pricing_model,
      cadence: c.cadence,
      stripePriceId: c.stripe_price_id,
      includedQuantity: c.included_quantity,
      overageStartsAt: c.overage_starts_at,
      cap: c.cap,
      quotaResetInterval: c.quota_reset_interval,
      meteredUnitKey: c.metered_unit_key,
      backfilledAt: c.backfilled_at,
    }));
    set({ priceCohortComponents: components });
  },

  createWorkspace: async (name, slug, userId, ownerDisplayName = null, ownerTimezone = 'UTC') => {
    // One creation path, shared with the per-identity bootstrap. Everything a
    // workspace needs is written in a single transaction there; the browser
    // could only manage a sequence of PostgREST calls, and a failure part way
    // through left a half-built workspace standing.
    const { data: created, error } = await coreApi('/workspaces', {
      name,
      slug,
      owner_display_name: ownerDisplayName,
      timezone: ownerTimezone,
    });
    if (error) {
      if (said(error, 'workspaces_slug_key')) {
        throw new Error('Please use a different workspace identifier');
      }
      if (said(error, 'workspaces_personal_slug_check')) {
        throw new Error('A workspace identifier cannot contain two hyphens in a row');
      }
      throw new Error(error.details || error.message);
    }
    const newId = (created?.[0] as { id?: string } | undefined)?.id;

    const { data: row, error: readError } = await supabase
      .from('workspaces').select('*').eq('id', newId as string).single();
    if (readError || !row) {
      throw new Error(readError?.message ?? 'The workspace was created but could not be read');
    }

    const workspace: Workspace = {
      id: row.id, name: row.name, slug: row.slug, ownerUserId: row.owner_user_id,
      status: (row as any).status ?? 'active',
      subscriptionTier: ((row as any).subscription_tier ?? 'free') as SubscriptionTier,
      subscriptionStatus: ((row as any).subscription_status ?? null) as SubscriptionStatus,
      stripeCustomerId: (row as any).stripe_customer_id ?? null,
      stripeSubscriptionId: (row as any).stripe_subscription_id ?? null,
      priceCohort: (row as any).price_cohort ?? null,
      purchasedSeats: (row as any).purchased_seats ?? null,
      renewalDate: (row as any).renewal_date ?? null,
      currentPeriodTotal: (row as any).current_period_total ?? null,
      cancelAtPeriodEnd: (row as any).cancel_at_period_end ?? false,
      seatOverageSince: (row as any).seat_overage_since ?? null,
      isPersonal: (row as any).is_personal ?? false,
    };

    set((s) => ({ workspaces: [...s.workspaces, workspace] }));

    return workspace;
  },



  acceptInvitation: async (invitationId) => {
    const { data, error } = await coreApi(`/invitations/${invitationId}/accept`, { member_id: crypto.randomUUID() });
    if (error) return { success: false, error: error.details || error.message };
    const result = data?.[0] as any;
    if (result?.error) return { success: false, error: result.error };
    return { success: true, workspaceSlug: result.workspace_slug };
  },

  declineInvitation: async (invitationId) => {
    const { data, error } = await coreApi(`/invitations/${invitationId}/decline`);
    if (error) return { success: false, error: error.details || error.message };
    const result = data?.[0] as any;
    if (!result?.success) return { success: false, error: result?.error ?? 'Failed to decline invitation' };
    return { success: true };
  },

  inviteMember: async (enteredEmail, role, personId) => {
    const email = enteredEmail.trim().toLowerCase();
    const { currentWorkspaceId, currentWorkspace } = get();
    if (!currentWorkspaceId) return { id: null, error: 'No workspace selected' };
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { id: null, error: 'Not authenticated' };

    const id = crypto.randomUUID();
    const createdAt = new Date().toISOString();
    const { data: sent, error: insErr } = await coreApi('/invitations', {
      id, workspace_id: currentWorkspaceId, email, role,
      ...(personId ? { person_id: personId } : {}),
    });
    if (insErr) {
      // Normalize the seat-cap trigger error to the same token the other seat mutations throw,
      // so callers can detect it consistently instead of getting a raw Postgres message.
      const error = said(insErr, 'seat_cap_exceeded') ? 'seat_cap_exceeded' : insErr.details || insErr.message;
      return { id: null, error };
    }
    const suppressed = (sent?.[0] as { suppressed?: 'already_member' | 'already_invited' } | undefined)?.suppressed;
    if (suppressed) return { id: null, suppressed };

    set((s) => ({
      invitations: [...s.invitations, {
        id, workspaceId: currentWorkspaceId, email, role: role as WorkspaceRole,
        invitedBy: user.id, status: 'pending', createdAt,
      }],
    }));

    let emailFailed = false;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();
      const inviterName =
        (profile?.full_name && profile.full_name.trim()) ||
        profile?.email ||
        user.email ||
        undefined;
      const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
      const acceptUrl = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(id)}`;
      const { error: emailErr } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'workspace-invitation',
          recipientEmail: email,
          idempotencyKey: `workspace-invite-${id}`,
          templateData: {
            workspaceName: currentWorkspace?.name ?? 'a workspace',
            inviterName,
            roleLabel,
            acceptUrl,
          },
        },
      });
      if (emailErr) emailFailed = true;
    } catch (err) {
      console.error('[inviteMember] failed to send invitation email', err);
      emailFailed = true;
    }

    return { id, emailFailed };
  },

  updateInvitationRole: async (invitationId, role) => {
    const { error } = await coreApi(`/invitations/${invitationId}`, { role }, 'PATCH');
    if (error) {
      if (said(error, 'seat_cap_exceeded')) throw new Error('seat_cap_exceeded');
      throw new Error(error.details || error.message);
    }
    set((s) => ({
      invitations: s.invitations.map((i) => i.id === invitationId ? { ...i, role } : i),
    }));
  },

  deleteInvitation: async (invitationId) => {
    await coreApi(`/invitations/${invitationId}`, {}, 'DELETE');
    set((s) => {
      const invitations = s.invitations.filter((i) => i.id !== invitationId);
      const ws = s.currentWorkspace;
      const nowCompliant = ws?.seatOverageSince && ws.purchasedSeats != null
        && countBillableSeats(s.members, invitations) <= ws.purchasedSeats;
      return {
        invitations,
        ...(nowCompliant ? {
          currentWorkspace: ws ? { ...ws, seatOverageSince: null } : null,
          workspaces: s.workspaces.map((w) => w.id === ws!.id ? { ...w, seatOverageSince: null } : w),
        } : {}),
      };
    });
  },

  resendInvitation: async (invitationId) => {
    const inv = get().invitations.find((i) => i.id === invitationId);
    if (!inv) return { error: 'Invitation not found' };
    const { currentWorkspace } = get();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: 'Not authenticated' };

    const { data: renewed, error: renewErr } = await coreApi(`/invitations/${invitationId}/resend`);
    if (renewErr?.code === 'not_found') return { error: 'Invitation not found' };
    if (renewErr) return { error: renewErr.details || renewErr.message };
    if (!renewed?.length) return { error: 'Invitation not found' };

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();
      const inviterName =
        (profile?.full_name && profile.full_name.trim()) ||
        profile?.email ||
        user.email ||
        undefined;
      const roleLabel = inv.role.charAt(0).toUpperCase() + inv.role.slice(1);
      const acceptUrl = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(inv.id)}`;
      const { error: emailErr } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'workspace-invitation',
          recipientEmail: inv.email,
          idempotencyKey: `workspace-invite-resend-${inv.id}-${Date.now()}`,
          templateData: {
            workspaceName: currentWorkspace?.name ?? 'a workspace',
            inviterName,
            roleLabel,
            acceptUrl,
          },
        },
      });
      if (emailErr) return { emailFailed: true };
    } catch {
      return { emailFailed: true };
    }
    return {};
  },

  reInvite: async (invitationId) => {
    const inv = get().invitations.find((i) => i.id === invitationId);
    if (!inv) return { error: 'Invitation not found' };
    const { currentWorkspace } = get();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: 'Not authenticated' };

    const createdAt = new Date().toISOString();
    const { error: updateErr } = await coreApi(`/invitations/${invitationId}/reinvite`);
    if (updateErr) {
      if (said(updateErr, 'seat_cap_exceeded')) return { error: 'seat_cap_exceeded' };
      return { error: updateErr.details || updateErr.message };
    }
    set((s) => ({
      invitations: s.invitations.map((i) => i.id === invitationId ? { ...i, status: 'pending', createdAt } : i),
    }));

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();
      const inviterName =
        (profile?.full_name && profile.full_name.trim()) ||
        profile?.email ||
        user.email ||
        undefined;
      const roleLabel = inv.role.charAt(0).toUpperCase() + inv.role.slice(1);
      const acceptUrl = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(inv.id)}`;
      const { error: emailErr } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'workspace-invitation',
          recipientEmail: inv.email,
          idempotencyKey: `workspace-invite-reinvite-${inv.id}-${Date.now()}`,
          templateData: { workspaceName: currentWorkspace?.name ?? 'a workspace', inviterName, roleLabel, acceptUrl },
        },
      });
      if (emailErr) return { emailFailed: true };
    } catch {
      return { emailFailed: true };
    }

    return {};
  },

  updateMemberRole: async (memberId, role) => {
    const { error } = await coreApi(`/members/${memberId}`, { role }, 'PATCH');
    if (error) {
      if (said(error, 'seat_cap_exceeded')) throw new Error('seat_cap_exceeded');
      throw new Error(error.details || error.message);
    }
    set((s) => {
      const members = s.members.map((m) => m.id === memberId ? { ...m, role } : m);
      const ws = s.currentWorkspace;
      const nowCompliant = role === 'observer' && ws?.seatOverageSince && ws.purchasedSeats != null
        && countBillableSeats(members, s.invitations) <= ws.purchasedSeats;
      return {
        members,
        ...(nowCompliant ? {
          currentWorkspace: ws ? { ...ws, seatOverageSince: null } : null,
          workspaces: s.workspaces.map((w) => w.id === ws!.id ? { ...w, seatOverageSince: null } : w),
        } : {}),
      };
    });
  },

  removeMember: async (memberId) => {
    await coreApi(`/members/${memberId}`, {}, 'DELETE');
    set((s) => {
      const members = s.members.filter((m) => m.id !== memberId);
      const ws = s.currentWorkspace;
      const nowCompliant = ws?.seatOverageSince && ws.purchasedSeats != null
        && countBillableSeats(members, s.invitations) <= ws.purchasedSeats;
      return {
        members,
        ...(nowCompliant ? {
          currentWorkspace: ws ? { ...ws, seatOverageSince: null } : null,
          workspaces: s.workspaces.map((w) => w.id === ws!.id ? { ...w, seatOverageSince: null } : w),
        } : {}),
      };
    });
  },

  linkMemberToPerson: async (memberId, personId) => {
    await coreApi(`/members/${memberId}`, { person_id: personId ?? null }, 'PATCH');
    set((s) => ({
      members: s.members.map((m) => m.id === memberId ? { ...m, personId } : m),
    }));
  },

  updateSettings: async (updates) => {
    const { settings, currentWorkspaceId } = get();
    if (!currentWorkspaceId) return;

    // Keep weekly alias in sync if a caller writes the legacy field.
    const normalized: Partial<WorkspaceSettings> = { ...updates };
    if (updates.meetingDataPeriod !== undefined && updates.meetingDataPeriodWeekly === undefined) {
      normalized.meetingDataPeriodWeekly = updates.meetingDataPeriod;
    }
    if (updates.meetingDataPeriodWeekly !== undefined) {
      normalized.meetingDataPeriod = updates.meetingDataPeriodWeekly;
    }

    if (!settings) {
      const id = crypto.randomUUID();
      const dbRow: any = { id, workspace_id: currentWorkspaceId };
      if (normalized.successThreshold !== undefined) dbRow.success_threshold = normalized.successThreshold;
      if (normalized.dangerZoneWeeks !== undefined) dbRow.danger_zone_weeks = normalized.dangerZoneWeeks;
      if (normalized.defaultMeetingLength !== undefined) dbRow.default_meeting_length = normalized.defaultMeetingLength;
      if (normalized.sectionLengths !== undefined) dbRow.section_lengths = normalized.sectionLengths;
      if (normalized.startOfWeek !== undefined) dbRow.start_of_week = normalized.startOfWeek;
      if (normalized.firstWeekRule !== undefined) dbRow.first_week_rule = normalized.firstWeekRule;
      if (normalized.meetingDataPeriod !== undefined) dbRow.meeting_data_period = normalized.meetingDataPeriod;
      if (normalized.meetingDataPeriodWeekly !== undefined) dbRow.meeting_data_period_weekly = normalized.meetingDataPeriodWeekly;
      if (normalized.meetingDataPeriodMonthly !== undefined) dbRow.meeting_data_period_monthly = normalized.meetingDataPeriodMonthly;
      if (normalized.meetingDataPeriodQuarterly !== undefined) dbRow.meeting_data_period_quarterly = normalized.meetingDataPeriodQuarterly;
      if (normalized.meetingDataPeriodYearly !== undefined) dbRow.meeting_data_period_yearly = normalized.meetingDataPeriodYearly;
      if (normalized.terminology !== undefined) dbRow.terminology = normalized.terminology;
      if (normalized.terminologyPlurals !== undefined) dbRow.terminology_plurals = normalized.terminologyPlurals;
      if (normalized.terminologyPreset !== undefined) dbRow.terminology_preset = normalized.terminologyPreset;
      if (normalized.agendaPreset !== undefined) dbRow.agenda_preset = normalized.agendaPreset;
      if (normalized.queuedStatesEnabled !== undefined) dbRow.queued_states_enabled = normalized.queuedStatesEnabled;
      if (normalized.cyclicalPlanning !== undefined) dbRow.cyclical_planning = normalized.cyclicalPlanning;
      if (normalized.cycleDecidedAt !== undefined) dbRow.cycle_decided_at = normalized.cycleDecidedAt;
      if (normalized.loosePermissions !== undefined) dbRow.loose_permissions = normalized.loosePermissions;
      if (normalized.visionTemplate !== undefined) dbRow.vision_template = normalized.visionTemplate;
      if (normalized.homeVisionFieldId !== undefined) dbRow.home_vision_field_id = normalized.homeVisionFieldId;
      if (normalized.homeValuesFieldId !== undefined) dbRow.home_values_field_id = normalized.homeValuesFieldId;
      if (normalized.showValueDescriptions !== undefined) dbRow.show_value_descriptions = normalized.showValueDescriptions;
      if (normalized.paceYellowThreshold !== undefined) dbRow.pace_yellow_threshold = normalized.paceYellowThreshold;
      if (normalized.paceRedThreshold !== undefined) dbRow.pace_red_threshold = normalized.paceRedThreshold;
      if (normalized.timezone !== undefined) dbRow.timezone = normalized.timezone;
      if (normalized.stakeholderFrameworkId !== undefined) dbRow.stakeholder_framework_id = normalized.stakeholderFrameworkId;
      if (normalized.stakeholderFrameworks !== undefined) dbRow.stakeholder_frameworks = normalized.stakeholderFrameworks;
      if (normalized.setupChecklist !== undefined) dbRow.setup_checklist = normalized.setupChecklist;
      if (normalized.showAvatars !== undefined) dbRow.show_avatars = normalized.showAvatars;
      if (normalized.orgChartBoardEnabled !== undefined) dbRow.org_chart_board_enabled = normalized.orgChartBoardEnabled;
      if (normalized.metricIndicatorsEnabled !== undefined) dbRow.metric_indicators_enabled = normalized.metricIndicatorsEnabled;
      if (normalized.aiInsightsEnabled !== undefined) dbRow.ai_insights_enabled = normalized.aiInsightsEnabled;
      if (normalized.aiInsightsVisibility !== undefined) dbRow.ai_insights_visibility = normalized.aiInsightsVisibility;
      if (normalized.connectedAppsEnabled !== undefined) dbRow.connected_apps_enabled = normalized.connectedAppsEnabled;
      if (normalized.connectedAppsWriteEnabled !== undefined) dbRow.connected_apps_write_enabled = normalized.connectedAppsWriteEnabled;
      const { workspace_id: _workspaceId, ...sentRow } = dbRow;
      const { error } = await coreApi(`/workspaces/${currentWorkspaceId}/settings`, sentRow, 'PATCH');
      if (error) throw error;
      if (get().currentWorkspaceId !== currentWorkspaceId) return;
      const weeklyDP = normalized.meetingDataPeriodWeekly ?? normalized.meetingDataPeriod ?? 'previous';
      set({
        settings: {
          id, workspaceId: currentWorkspaceId,
          successThreshold: normalized.successThreshold ?? DEFAULT_WORKSPACE_SCORECARD_SETTINGS.successThreshold,
          dangerZoneWeeks: normalized.dangerZoneWeeks ?? DEFAULT_WORKSPACE_SCORECARD_SETTINGS.dangerZoneWeeks,
          defaultMeetingLength: normalized.defaultMeetingLength ?? DEFAULT_WORKSPACE_MEETING_LENGTH,
          sectionLengths: normalized.sectionLengths ?? DEFAULT_WORKSPACE_SECTION_LENGTHS,
          startOfWeek: normalized.startOfWeek ?? 1,
          firstWeekRule: normalized.firstWeekRule ?? 'touching',
          meetingDataPeriod: weeklyDP,
          meetingDataPeriodWeekly: weeklyDP,
          meetingDataPeriodMonthly: normalized.meetingDataPeriodMonthly ?? 'previous',
          meetingDataPeriodQuarterly: normalized.meetingDataPeriodQuarterly ?? 'previous',
          meetingDataPeriodYearly: normalized.meetingDataPeriodYearly ?? 'previous',
          terminology: normalized.terminology ?? null,
          terminologyPlurals: normalized.terminologyPlurals ?? null,
          terminologyPreset: normalized.terminologyPreset ?? null,
          agendaPreset: normalized.agendaPreset ?? null,
          queuedStatesEnabled: normalized.queuedStatesEnabled ?? false,
          cyclicalPlanning: normalized.cyclicalPlanning ?? true,
          cycleDecidedAt: normalized.cycleDecidedAt ?? null,
          onboarding: EMPTY_ONBOARDING,
          loosePermissions: normalized.loosePermissions ?? false,
          visionTemplate: normalized.visionTemplate ?? null,
          homeVisionFieldId: normalized.homeVisionFieldId ?? null,
          homeValuesFieldId: normalized.homeValuesFieldId ?? null,
          showValueDescriptions: normalized.showValueDescriptions ?? true,
          paceYellowThreshold: normalized.paceYellowThreshold ?? 10,
          paceRedThreshold: normalized.paceRedThreshold ?? 25,
          timezone: normalized.timezone ?? 'UTC',
          stakeholderFrameworkId: normalized.stakeholderFrameworkId ?? 'raci',
          stakeholderFrameworks: normalized.stakeholderFrameworks ?? [],
          setupChecklist: normalized.setupChecklist ?? null,
          showAvatars: normalized.showAvatars ?? false,
          orgChartBoardEnabled: normalized.orgChartBoardEnabled ?? false,
          metricIndicatorsEnabled: normalized.metricIndicatorsEnabled ?? true,
          // Mirrors the column default, which is TRUE for a row inserted after
          // the AI-insights migration.
          aiInsightsEnabled: normalized.aiInsightsEnabled ?? true,
          // Mirrors the column default for a row inserted after #293.
          aiInsightsVisibility: normalized.aiInsightsVisibility ?? 'restricted',
          connectedAppsEnabled: normalized.connectedAppsEnabled ?? false,
          connectedAppsWriteEnabled: normalized.connectedAppsWriteEnabled ?? false,
        },
      });
    } else {
      const dbUpdates: any = {};
      if (normalized.successThreshold !== undefined) dbUpdates.success_threshold = normalized.successThreshold;
      if (normalized.dangerZoneWeeks !== undefined) dbUpdates.danger_zone_weeks = normalized.dangerZoneWeeks;
      if (normalized.defaultMeetingLength !== undefined) dbUpdates.default_meeting_length = normalized.defaultMeetingLength;
      if (normalized.sectionLengths !== undefined) dbUpdates.section_lengths = normalized.sectionLengths;
      if (normalized.startOfWeek !== undefined) dbUpdates.start_of_week = normalized.startOfWeek;
      if (normalized.firstWeekRule !== undefined) dbUpdates.first_week_rule = normalized.firstWeekRule;
      if (normalized.meetingDataPeriod !== undefined) dbUpdates.meeting_data_period = normalized.meetingDataPeriod;
      if (normalized.meetingDataPeriodWeekly !== undefined) dbUpdates.meeting_data_period_weekly = normalized.meetingDataPeriodWeekly;
      if (normalized.meetingDataPeriodMonthly !== undefined) dbUpdates.meeting_data_period_monthly = normalized.meetingDataPeriodMonthly;
      if (normalized.meetingDataPeriodQuarterly !== undefined) dbUpdates.meeting_data_period_quarterly = normalized.meetingDataPeriodQuarterly;
      if (normalized.meetingDataPeriodYearly !== undefined) dbUpdates.meeting_data_period_yearly = normalized.meetingDataPeriodYearly;
      if (normalized.terminology !== undefined) dbUpdates.terminology = normalized.terminology;
      if (normalized.terminologyPlurals !== undefined) dbUpdates.terminology_plurals = normalized.terminologyPlurals;
      if (normalized.terminologyPreset !== undefined) dbUpdates.terminology_preset = normalized.terminologyPreset;
      if (normalized.agendaPreset !== undefined) dbUpdates.agenda_preset = normalized.agendaPreset;
      if (normalized.queuedStatesEnabled !== undefined) dbUpdates.queued_states_enabled = normalized.queuedStatesEnabled;
      if (normalized.cyclicalPlanning !== undefined) dbUpdates.cyclical_planning = normalized.cyclicalPlanning;
      if (normalized.cycleDecidedAt !== undefined) dbUpdates.cycle_decided_at = normalized.cycleDecidedAt;
      if (normalized.loosePermissions !== undefined) dbUpdates.loose_permissions = normalized.loosePermissions;
      if (normalized.visionTemplate !== undefined) dbUpdates.vision_template = normalized.visionTemplate;
      if (normalized.homeVisionFieldId !== undefined) dbUpdates.home_vision_field_id = normalized.homeVisionFieldId;
      if (normalized.homeValuesFieldId !== undefined) dbUpdates.home_values_field_id = normalized.homeValuesFieldId;
      if (normalized.showValueDescriptions !== undefined) dbUpdates.show_value_descriptions = normalized.showValueDescriptions;
      if (normalized.paceYellowThreshold !== undefined) dbUpdates.pace_yellow_threshold = normalized.paceYellowThreshold;
      if (normalized.paceRedThreshold !== undefined) dbUpdates.pace_red_threshold = normalized.paceRedThreshold;
      if (normalized.timezone !== undefined) dbUpdates.timezone = normalized.timezone;
      if (normalized.stakeholderFrameworkId !== undefined) dbUpdates.stakeholder_framework_id = normalized.stakeholderFrameworkId;
      if (normalized.stakeholderFrameworks !== undefined) dbUpdates.stakeholder_frameworks = normalized.stakeholderFrameworks;
      if (normalized.setupChecklist !== undefined) dbUpdates.setup_checklist = normalized.setupChecklist;
      if (normalized.showAvatars !== undefined) dbUpdates.show_avatars = normalized.showAvatars;
      if (normalized.orgChartBoardEnabled !== undefined) dbUpdates.org_chart_board_enabled = normalized.orgChartBoardEnabled;
      if (normalized.metricIndicatorsEnabled !== undefined) dbUpdates.metric_indicators_enabled = normalized.metricIndicatorsEnabled;
      if (normalized.aiInsightsEnabled !== undefined) dbUpdates.ai_insights_enabled = normalized.aiInsightsEnabled;
      if (normalized.aiInsightsVisibility !== undefined) dbUpdates.ai_insights_visibility = normalized.aiInsightsVisibility;
      if (normalized.connectedAppsEnabled !== undefined) dbUpdates.connected_apps_enabled = normalized.connectedAppsEnabled;
      if (normalized.connectedAppsWriteEnabled !== undefined) dbUpdates.connected_apps_write_enabled = normalized.connectedAppsWriteEnabled;
      const { data, error } = await coreApi(`/workspaces/${currentWorkspaceId}/settings`, dbUpdates, 'PATCH');
      if (error) throw error;
      if (!data?.[0]) throw new Error('Workspace settings update was not applied');
      if (
        get().currentWorkspaceId !== currentWorkspaceId
        || get().settings?.id !== settings.id
      ) return;
      set((state) => ({ settings: state.settings ? { ...state.settings, ...normalized } : null }));
    }
  },

  applyPreset: async (presetKey, replace = {}) => {
    const { currentWorkspace, currentWorkspaceId, settings, meetingTypes } = get();
    if (!currentWorkspaceId || !settings) return { visionApplied: false };
    if (presetKey !== null) {
      if (!isBuiltInPreset(presetKey)) throw new Error(`Unknown preset: ${presetKey}`);
      if (!workspaceHasFeature('presets', currentWorkspace?.subscriptionTier ?? 'free', !!currentWorkspace?.isPersonal)) {
        throw new Error('Applying a preset needs the Launch plan.');
      }
    }
    const ctx: PresetContext = {
      settings,
      meetingTypes,
      visions: [],
      tier: currentWorkspace?.subscriptionTier ?? 'free',
      isPersonal: !!currentWorkspace?.isPersonal,
    };
    const write = presetWrite(planPreset(presetKey, ctx), replace, ctx);
    if (!write.terminology && !write.agendas && !write.vision) return { visionApplied: false };

    const terminologyPreset = terminologyPresetFor(presetKey);
    const agendaPreset = agendaPresetFor(presetKey);
    const visionTemplate = visionTemplateFor(presetKey);
    const { data, error } = await coreApi(`/workspaces/${currentWorkspaceId}/preset`, {
      terminology: write.terminology,
      terminology_preset: terminologyPreset,
      clear_terms: write.clearTerms,
      agendas: write.agendas,
      agenda_preset: agendaPreset,
      clear_labels: write.clearLabels,
      clear_sections: write.clearSections,
      vision: write.vision,
      vision_template: visionTemplate,
    });
    if (error) throw error;
    const visionApplied = (data?.[0] as { vision_applied?: boolean } | undefined)?.vision_applied === true;

    const updates: Partial<WorkspaceSettings> = {};
    if (write.terminology) updates.terminologyPreset = terminologyPreset;
    if (write.clearTerms) { updates.terminology = null; updates.terminologyPlurals = null; }
    if (write.agendas) updates.agendaPreset = agendaPreset;
    if (visionApplied) updates.visionTemplate = visionTemplate;

    if (get().currentWorkspaceId === currentWorkspaceId) {
      const labels = new Set(write.clearLabels);
      const sections = new Set(write.clearSections);
      set((s) => ({
        settings: s.settings ? { ...s.settings, ...updates } : null,
        meetingTypes: s.meetingTypes.map((t) => t.id && (labels.has(t.id) || sections.has(t.id))
          ? { ...t, label: labels.has(t.id) ? null : t.label, sections: sections.has(t.id) ? null : t.sections }
          : t),
      }));
    }

    return { visionApplied };
  },

  saveOnboarding: async (patch, workspaceId) => {
    const targetId = workspaceId ?? get().currentWorkspaceId;
    if (!targetId) return;
    const row: Record<string, unknown> = {};
    for (const key of Object.keys(patch) as (keyof OnboardingDraft)[]) {
      if (patch[key] !== undefined) row[ONBOARDING_COLUMNS[key]] = patch[key];
    }
    if (Object.keys(row).length === 0) return;
    const { data, error } = await coreApi(`/workspaces/${targetId}/settings`, row, 'PATCH');
    if (error) throw error;
    if (!data?.[0]) throw new Error('The setup answers were not saved');
    if (get().currentWorkspaceId !== targetId) return;
    set((state) => ({
      settings: state.settings ? { ...state.settings, onboarding: { ...state.settings.onboarding, ...patch } } : null,
    }));
  },

  addMeetingType: async (type) => {
    const wsId = get().currentWorkspaceId;
    if (!wsId) return;
    const { data: created, error } = await coreApi('/records/meeting-types', {
      workspace_id: wsId,
      // Omitted team_id defaults to the workspace root via default_team_id();
      // a caller creating a type on a team passes it explicitly.
      ...(type.teamId ? { team_id: type.teamId } : {}),
      key: type.key,
      label: type.label,
      built_in: type.builtIn,
      sort_order: type.sortOrder,
      sections: type.sections as any,
      fathom_meeting_title: type.fathomMeetingTitle ?? null,
      recurrence: type.recurrence ?? null,
      notification_timing_hours: type.notificationTimingHours,
      prep_email_enabled: type.prepEmailEnabled,
      default_invitees: type.defaultInvitees,
      default_host_id: type.defaultHostId ?? null,
    });
    if (error) throw error;
    const data = created?.[0];
    // A blocked insert returns no row and no error. Saying so beats a "Saved"
    // toast over a write that never landed.
    if (!data) throw new Error('Could not create the meeting type here.');
    set((s) => ({ meetingTypes: [...s.meetingTypes, deserializeMeetingType(data)] }));
  },

  updateMeetingType: async (id, updates) => {
    set((s) => ({
      meetingTypes: s.meetingTypes.map((t) => t.id === id ? { ...t, ...updates } : t),
    }));
    const dbUpdates: any = { updated_at: new Date().toISOString() };
    if (updates.label !== undefined) dbUpdates.label = updates.label;
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
    if (updates.sections !== undefined) dbUpdates.sections = updates.sections;
    if (updates.fathomMeetingTitle !== undefined) dbUpdates.fathom_meeting_title = updates.fathomMeetingTitle ?? null;
    if (updates.recurrence !== undefined) dbUpdates.recurrence = updates.recurrence ?? null;
    if (updates.notificationTimingHours !== undefined) dbUpdates.notification_timing_hours = updates.notificationTimingHours;
    if (updates.prepEmailEnabled !== undefined) dbUpdates.prep_email_enabled = updates.prepEmailEnabled;
    if (updates.defaultInvitees !== undefined) dbUpdates.default_invitees = updates.defaultInvitees;
    if (updates.defaultHostId !== undefined) dbUpdates.default_host_id = updates.defaultHostId ?? null;
    const { error } = await coreApi(`/records/meeting-types/${id}`, dbUpdates, 'PATCH');
    if (error) throw error;
  },

  deleteMeetingType: async (id) => {
    const before = get().meetingTypes;
    set((s) => ({ meetingTypes: s.meetingTypes.filter((t) => t.id !== id) }));
    applyWrite(coreApi(`/records/meeting-types/${id}`, {}, 'DELETE'), {
      noun: 'this meeting type',
      context: 'deleteMeetingType',
      operation: 'delete',
      // The whole list, so a restore keeps the order the user arranged.
      rollback: () => set({ meetingTypes: before }),
    });
  },

  transferOwnership: async (newOwnerMemberId) => {
    const { currentWorkspaceId, members, currentWorkspace } = get();
    if (!currentWorkspaceId || !currentWorkspace) return;

    const newOwner = members.find((m) => m.id === newOwnerMemberId);
    if (!newOwner || newOwner.role !== 'admin') return;

    const currentOwner = members.find((m) => m.role === 'owner');
    const { error } = await coreApi(`/workspaces/${currentWorkspaceId}/transfer-ownership`, { member_id: newOwnerMemberId });
    if (error) throw new Error(error.details || error.message);

    set((s) => ({
      currentWorkspace: { ...s.currentWorkspace!, ownerUserId: newOwner.userId },
      members: s.members.map((m) => {
        if (m.id === newOwnerMemberId) return { ...m, role: 'owner' as WorkspaceRole };
        if (m.role === 'owner') return { ...m, role: 'admin' as WorkspaceRole };
        return m;
      }),
      myRole: currentOwner?.userId === newOwner.userId ? 'owner' : (s.myRole === 'owner' ? 'admin' : s.myRole),
    }));
  },

  deleteWorkspace: async (workspaceId) => {
    const { error } = await coreApi(`/workspaces/${workspaceId}`, {}, 'DELETE');
    if (error) throw new Error(error.details || error.message);
    set((s) => {
      const isCurrent = s.currentWorkspaceId === workspaceId;
      return {
        workspaces: s.workspaces.filter((w) => w.id !== workspaceId),
        currentWorkspaceId: isCurrent ? null : s.currentWorkspaceId,
        currentWorkspace: isCurrent ? null : s.currentWorkspace,
        members: isCurrent ? [] : s.members,
        invitations: isCurrent ? [] : s.invitations,
        settings: isCurrent ? null : s.settings,
        myRole: isCurrent ? null : s.myRole,
      };
    });
  },

}));

// ─── Helpers ───────────────────────────────────────────────

import type { PeriodConfig, MeetingDataPeriod } from '@/modules/cycles/period-utils';
import { DEFAULT_PERIOD_CONFIG } from '@/modules/cycles/period-utils';
import { localDateStr, todayInTz } from '@/shared/lib/date-utils';

/** Selector hook: current workspace's period config (start of week, first-week rule, and the workspace's today).
 *  Selects primitives separately so the returned object only changes when values change. */
export function usePeriodConfig(): PeriodConfig {
  const startOfWeek = useWorkspaceStore((s) => s.settings?.startOfWeek ?? DEFAULT_PERIOD_CONFIG.startOfWeek);
  const firstWeekRule = useWorkspaceStore((s) => s.settings?.firstWeekRule ?? DEFAULT_PERIOD_CONFIG.firstWeekRule);
  const timezone = useWorkspaceStore((s) => s.settings?.timezone);
  const today = timezone ? todayInTz(timezone) : localDateStr();
  return useMemo(() => ({ startOfWeek, firstWeekRule, today }), [startOfWeek, firstWeekRule, today]);
}

/** Selector hook: workspace's meeting data-period preference for a given cadence.
 *  Defaults to weekly preference when no cadence is supplied (back-compat). */
export function useMeetingDataPeriod(cadence: Cadence = 'weekly'): MeetingDataPeriod {
  return useWorkspaceStore((s) => {
    if (cadence === 'monthly') return s.settings?.meetingDataPeriodMonthly ?? 'previous';
    if (cadence === 'quarterly') return s.settings?.meetingDataPeriodQuarterly ?? 'previous';
    if (cadence === 'yearly') return s.settings?.meetingDataPeriodYearly ?? 'previous';
    return s.settings?.meetingDataPeriodWeekly ?? s.settings?.meetingDataPeriod ?? 'previous';
  });
}

export function useMeetingTypes(): MeetingType[] {
  return useWorkspaceStore((s) => s.meetingTypes);
}

/** @deprecated use useMeetingTypes() */
export function useMeetingTypeConfigs(): MeetingType[] {
  return useWorkspaceStore((s) => s.meetingTypes);
}

/**
 * Returns the meeting types available for the current subscription tier.
 * Free workspaces get only the weekly and quarterly built-ins (non-configurable).
 * Pro workspaces get all configured types.
 */
/** Returns BUILTIN_SECTION_KEYS as MeetingSectionDef objects. */
export function useBuiltinSections(): MeetingSectionDef[] {
  return BUILTIN_SECTION_KEYS
    .map((key) => WEEKLY_MEETING_SECTIONS.find((s) => s.key === key) ?? { key, label: key, length: 5, custom: false as const });
}

/** Every built-in section a type can hold, including ones off its default agenda. */
export function sectionLibraryFor(typeKey: string): MeetingSectionDef[] {
  if (typeKey === 'quarterly') {
    return [
      ...QUARTERLY_MEETING_SECTIONS,
      { key: 'quarterly-tasks', label: 'To-Dos', length: 20, custom: false },
    ];
  }
  if (typeKey === 'yearly') return ANNUAL_MEETING_SECTIONS;
  return BUILTIN_SECTION_KEYS
    .map((key) => WEEKLY_MEETING_SECTIONS.find((s) => s.key === key) ?? { key, label: key, length: 5, custom: false as const });
}

/**
 * Selector hook: the current workspace's per_seat pricing component for a given cadence
 * (defaults to the workspace's current billing cadence if known, else 'monthly'). Returns
 * undefined if the cohort has no per_seat component (flat-rate-only cohorts).
 * Seat structure (included seats, overage start, cap) always comes from this row — never
 * hardcode seat numbers in components.
 */
export function usePerSeatComponent(cadence: 'monthly' | 'annual' = 'monthly'): PriceCohortComponent | undefined {
  return useWorkspaceStore((s) =>
    s.priceCohortComponents.find((c) => c.pricingModel === 'per_seat' && c.cadence === cadence)
  );
}

/**
 * The workspace's effective seat cap: purchased Stripe seats once subscribed, otherwise its
 * cohort's own cap (e.g. the free cohort's cap=1) — null means uncapped. Use this anywhere a
 * seat limit needs enforcing; never read purchasedSeats alone, since that's null pre-subscription.
 */
export function useEffectiveSeatCap(): number | null {
  const purchasedSeats = useWorkspaceStore((s) => s.currentWorkspace?.purchasedSeats ?? null);
  const monthlyPerSeat = usePerSeatComponent('monthly');
  const annualPerSeat = usePerSeatComponent('annual');
  // `purchased_seats` carries a flat rung's cap too — the webhook writes it from
  // the cohort's seats quota rather than from the Stripe quantity, which on a
  // flat price is always 1. So this stays one number on the row, and the
  // per-seat components remain the fallback for a workspace that has never
  // completed a checkout.
  return purchasedSeats ?? monthlyPerSeat?.cap ?? annualPerSeat?.cap ?? null;
}
